import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, mergeMap, of, throwError } from 'rxjs';

import {
  AccountStateResponse,
  AccountAccessStateResponse,
  AuthRequest,
  ChangePasswordRequest,
  ExternalAuthCallbackPayload,
  ExternalAuthProvider,
  ExternalAuthProviderOption,
  ExternalAuthRegistrationPayload,
  ForgotPasswordRequest,
  LinkedExternalProvider,
  LoginResponse,
  OwnerAccountStatusResponse,
  OwnerStatusHistoryItem,
  RefreshTokenRequest,
  RegisterRequest,
  ResendEmailConfirmationRequest,
  ResetPasswordRequest,
  User
} from '@app/core/models';
import { StorageService } from './storage.service';
import { environment } from '@env/environment';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapApiData = <T>(response: unknown): T => {
  if (isRecord(response) && 'data' in response) {
    return response['data'] as T;
  }

  return response as T;
};

const isUserPayload = (value: unknown): value is User =>
  isRecord(value) &&
  typeof value['id'] === 'string' &&
  typeof value['email'] === 'string' &&
  typeof value['fullName'] === 'string' &&
  typeof value['role'] === 'string';

const isAccountStatePayload = (value: unknown): value is AccountStateResponse =>
  isRecord(value) &&
  typeof value['userId'] === 'string' &&
  typeof value['email'] === 'string' &&
  typeof value['fullName'] === 'string' &&
  typeof value['role'] === 'string' &&
  typeof value['emailConfirmed'] === 'boolean';

const isLoginResponsePayload = (value: unknown): value is LoginResponse =>
  isRecord(value) && typeof value['token'] === 'string' && isUserPayload(value['user']);

const isExternalAuthProvider = (value: unknown): value is ExternalAuthProvider =>
  value === 'google' || value === 'microsoft' || value === 'apple' || value === 'facebook';

const DEFAULT_EXTERNAL_PROVIDERS: ExternalAuthProviderOption[] = [
  { provider: 'google', label: 'Google', available: true },
  { provider: 'microsoft', label: 'Microsoft', available: true },
  { provider: 'apple', label: 'Apple', available: true },
  { provider: 'facebook', label: 'Facebook', available: true }
];

const isLinkedProviderPayload = (value: unknown): value is LinkedExternalProvider =>
  isRecord(value) && isExternalAuthProvider(value['provider']);

const normalizeExternalProviders = (value: unknown): ExternalAuthProviderOption[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_EXTERNAL_PROVIDERS;
  }

  return value.reduce<ExternalAuthProviderOption[]>((providers, item) => {
    if (typeof item === 'string' && isExternalAuthProvider(item)) {
      providers.push({
        provider: item,
        label: DEFAULT_EXTERNAL_PROVIDERS.find((entry) => entry.provider === item)?.label ?? item,
        available: true
      });
      return providers;
    }

    if (!isRecord(item) || !isExternalAuthProvider(item['provider'])) {
      return providers;
    }

    providers.push({
      provider: item['provider'],
      label:
        typeof item['label'] === 'string'
          ? item['label']
          : typeof item['displayName'] === 'string'
            ? item['displayName']
            : DEFAULT_EXTERNAL_PROVIDERS.find((entry) => entry.provider === item['provider'])?.label ??
              item['provider'],
      available:
        typeof item['available'] === 'boolean'
          ? item['available']
          : typeof item['enabled'] === 'boolean'
            ? item['enabled']
            : true
    });

    return providers;
  }, []);
};

const normalizeLinkedProviders = (value: unknown): LinkedExternalProvider[] =>
  Array.isArray(value) ? value.filter(isLinkedProviderPayload) : [];

const extractExternalToken = (payload: ExternalAuthCallbackPayload): string | null =>
  payload.token ?? payload.authToken ?? payload.accessToken ?? null;

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly authBaseUrl = `${environment.apiBaseUrl}/auth`;
  private readonly accountBaseUrl = `${environment.apiBaseUrl}/account`;
  private readonly ownerBaseUrl = `${environment.apiBaseUrl}/owner`;

  register(payload: RegisterRequest, idempotencyKey?: string) {
    return this.http.post<unknown>(`${this.authBaseUrl}/register`, payload, {
      headers: idempotencyKey ? new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) : undefined
    }).pipe(
      mergeMap((response) => {
        const unwrapped = unwrapApiData<unknown>(response);
        if (isLoginResponsePayload(unwrapped)) {
          return of(unwrapped);
        }

        return throwError(() => new Error('Invalid registration response payload'));
      })
    );
  }

  login(payload: AuthRequest) {
    return this.http.post<unknown>(`${this.authBaseUrl}/login`, payload).pipe(
      mergeMap((response) => {
        const unwrapped = unwrapApiData<unknown>(response);
        if (isLoginResponsePayload(unwrapped)) {
          return of(unwrapped);
        }

        return throwError(() => new Error('Invalid auth response payload'));
      })
    );
  }

  logout(refreshToken: string) {
    return this.http.post<void>(`${this.authBaseUrl}/logout`, { refreshToken });
  }

  refresh(payload: RefreshTokenRequest) {
    return this.http.post<unknown>(`${this.authBaseUrl}/refresh`, payload).pipe(
      mergeMap((response) => {
        const unwrapped = unwrapApiData<unknown>(response);
        if (isLoginResponsePayload(unwrapped)) {
          return of(unwrapped);
        }

        if (isRecord(unwrapped) && typeof unwrapped['token'] === 'string') {
          const token = unwrapped['token'];
          const refreshToken =
            typeof unwrapped['refreshToken'] === 'string' ? unwrapped['refreshToken'] : undefined;

          this.storage.setToken(token);
          if (refreshToken) {
            this.storage.setRefreshToken(refreshToken);
          }

          return this.me().pipe(
            map((user) => ({
              token,
              refreshToken,
              user,
              linkedProviders: normalizeLinkedProviders(unwrapped['linkedProviders'])
            }))
          );
        }

        return throwError(() => new Error('Invalid refresh response payload'));
      })
    );
  }

  me() {
    return this.http.get<unknown>(`${this.authBaseUrl}/me`).pipe(
      mergeMap((response) => {
        const unwrapped = unwrapApiData<unknown>(response);
        if (isUserPayload(unwrapped)) {
          return of(unwrapped);
        }

        return throwError(() => new Error('Invalid session payload'));
      })
    );
  }

  getAccountState() {
    return this.http.get<unknown>(`${this.accountBaseUrl}/state`).pipe(
      mergeMap((response) => {
        const unwrapped = unwrapApiData<unknown>(response);
        if (isAccountStatePayload(unwrapped)) {
          return of(unwrapped);
        }

        return throwError(() => new Error('Invalid account state payload'));
      })
    );
  }

  getAccountAccessState() {
    return this.http
      .get<unknown>(`${this.accountBaseUrl}/access-state`)
      .pipe(map((response) => unwrapApiData<AccountAccessStateResponse>(response)));
  }

  getOwnerAccountStatus() {
    return this.http
      .get<unknown>(`${this.ownerBaseUrl}/account-status`)
      .pipe(map((response) => unwrapApiData<OwnerAccountStatusResponse>(response)));
  }

  getOwnerStatusHistory() {
    return this.http.get<unknown>(`${this.ownerBaseUrl}/account-status/history`).pipe(
      map((response) => {
        const unwrapped = unwrapApiData<unknown>(response);
        if (Array.isArray(unwrapped)) {
          return unwrapped as OwnerStatusHistoryItem[];
        }

        if (isRecord(unwrapped) && Array.isArray(unwrapped['items'])) {
          return unwrapped['items'] as OwnerStatusHistoryItem[];
        }

        if (isRecord(unwrapped) && Array.isArray(unwrapped['history'])) {
          return unwrapped['history'] as OwnerStatusHistoryItem[];
        }

        return [];
      })
    );
  }

  resendEmailConfirmation(payload: ResendEmailConfirmationRequest) {
    return this.http.post<void>(`${this.authBaseUrl}/resend-email-confirmation`, payload);
  }

  confirmEmail(queryParams: Record<string, string>) {
    let params = new HttpParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });

    return this.http.get<void>(`${this.authBaseUrl}/confirm-email`, { params });
  }

  forgotPassword(payload: ForgotPasswordRequest) {
    return this.http.post<void>(`${this.authBaseUrl}/forgot-password`, payload);
  }

  resetPassword(payload: ResetPasswordRequest) {
    return this.http.post<void>(`${this.authBaseUrl}/reset-password`, payload);
  }

  changePassword(payload: ChangePasswordRequest) {
    return this.http.post<void>(`${this.authBaseUrl}/change-password`, payload);
  }

  getExternalProviders() {
    if (!environment.externalAuthEnabled) {
      return of<ExternalAuthProviderOption[]>([]);
    }

    return this.http.get<unknown>(`${this.authBaseUrl}/external/providers`).pipe(
      map((response) => normalizeExternalProviders(response)),
      catchError(() => of(DEFAULT_EXTERNAL_PROVIDERS))
    );
  }

  startExternalLogin(provider: ExternalAuthProvider, role: 'owner' | 'partner'): void {
    if (!environment.externalAuthEnabled) {
      throw new Error('External sign-in is disabled in this environment.');
    }

    const url = new URL(`${this.authBaseUrl}/external/${provider}/start`, window.location.origin);
    url.searchParams.set('role', role);
    url.searchParams.set('returnUrl', this.getExternalReturnUrl());
    window.location.assign(url.toString());
  }

  completeExternalAuth(payload: ExternalAuthCallbackPayload) {
    const token = extractExternalToken(payload);

    if (!token) {
      return throwError(
        () =>
          new Error(
            'External sign-in completed without a session token. Ensure the backend redirects the frontend callback with token, authToken, or accessToken.'
          )
      );
    }

    this.storage.setToken(token);

    return this.me().pipe(
      map((user) => ({
        token,
        user,
        linkedProviders: normalizeLinkedProviders((payload as Record<string, unknown>)['linkedProviders'])
      })),
      catchError((error) => {
        this.storage.clearToken();
        return throwError(() => error);
      })
    );
  }

  completeExternalRegistration(payload: ExternalAuthRegistrationPayload) {
    return this.http.post<unknown>(`${this.authBaseUrl}/external/complete-registration`, payload).pipe(
      mergeMap((response) => {
        if (isLoginResponsePayload(response)) {
          return of({
            ...response,
            linkedProviders: normalizeLinkedProviders(
              (response as unknown as Record<string, unknown>)['linkedProviders']
            )
          });
        }

        return throwError(() => new Error('Invalid external registration response payload'));
      })
    );
  }

  getLinkedProviders() {
    return this.http.get<unknown>(`${this.authBaseUrl}/external/linked-providers`).pipe(
      map((response) => normalizeLinkedProviders(response)),
      catchError(() => of<LinkedExternalProvider[]>([]))
    );
  }

  private getExternalReturnUrl(): string {
    return new URL('/auth/external/callback', window.location.origin).toString();
  }
}
